<template>
  <v-card style="min-height:400px">
    <v-row>
      <v-col :cols="12">
        <v-card elevation="3" style="margin:3px">
          <v-subheader>Course Listings</v-subheader>
          <v-divider></v-divider>
          <v-tabs v-model="searchTab">
            <v-tab>Recommended</v-tab>
            <v-tab>Search</v-tab>
          </v-tabs>
          <v-tabs-items v-model="searchTab">
            <v-tab-item>
              <recommended-courses :major="major" :schedule="schedule" :course-search-filter="courseSearchFilter" @addCourse="(course) => schedule.push(course)" @search="searchTab = 1"></recommended-courses>
            </v-tab-item>
            <v-tab-item>
              <search-courses :schedule="schedule" :course-search-filter="courseSearchFilter" @addCourse="(course) => schedule.push(course)"></search-courses>
            </v-tab-item>
          </v-tabs-items>
        </v-card>
      </v-col>
      <v-col :cols="12">
        <v-card elevation="3" class="mb-2" style="margin:3px;">
          <v-subheader>Selected Courses</v-subheader>
          <v-divider></v-divider>
          <v-tabs v-model="scheduleTab">
            <v-tab>List View</v-tab>
            <v-tab>Calendar View</v-tab>
          </v-tabs>
          <v-tabs-items v-model="scheduleTab">
            <v-tab-item>
              <v-list v-if="schedule.length > 0">
                <template v-for="(course, index) in schedule">
                  <v-list-item :key="'course-' + index">
                    <v-list-item-content>
                      <v-list-item-title style="white-space:normal">{{ course.title }}</v-list-item-title>
                      <v-list-item-subtitle>Meets: <v-chip v-for="{ _id, days, startTime, endTime } in course.meetingBase" :key="_id" label outlined>{{ dayDisplay(days) }} {{ startTime }} - {{ endTime }}</v-chip></v-list-item-subtitle>
                    </v-list-item-content>
                    <v-list-item-action v-if="!['COR100','COR150'].includes(course.title.substr(0, 6))">
                      <v-icon color="error" @click="schedule.splice(index, 1)">fal fa-times-circle</v-icon>
                    </v-list-item-action>
                  </v-list-item>
                  <v-divider v-if="index < schedule.length" :key="'div-' + index"></v-divider>
                </template>
              </v-list>
            </v-tab-item>
            <v-tab-item>
              <v-calendar ref="calendar" :weekdays="weekdays" :start="startDate" :events="events" type="week" style="max-height:600px" @click:event="showCourse"></v-calendar>
              <v-menu v-model="courseDetailOpen" :close-on-content-click="false" :activator="selectedElement" offset-x>
                <v-card color="grey lighten-4" min-width="350px" flat>
                  <v-toolbar color="info" dark>
                    <v-toolbar-title v-html="selectedCourse.name"></v-toolbar-title>
                  </v-toolbar>
                  <v-card-text>
                    <div v-if="selectedCourse.section">Section: {{ selectedCourse.section }}</div>
                    <div>Date/Time:
                      <v-chip v-for="{ _id, days, startTime, endTime } in selectedCourse.meetingBase" :key="_id" class="mr-1" label outlined>{{ days }} {{ startTime }} - {{ endTime }}</v-chip>
                    </div>
                    <div v-if="selectedCourse.notes" v-html="selectedCourse.notes"></div>
                  </v-card-text>
                  <v-card-actions>
                    <v-btn color="secondary" text @click="courseDetailOpen = false">Close</v-btn>
                    <v-btn v-if="selectedCourse && selectedCourse.name && selectedCourse.name.substr(0, 6) !== 'COR100'" color="error" text @click="removeCourse(selectedCourse)">Remove Course</v-btn>
                  </v-card-actions>
                </v-card>
              </v-menu>
            </v-tab-item>
          </v-tabs-items>
          <v-card-text>Selected Credit Hours: {{ hours }}</v-card-text>
        </v-card>
        <template v-if="major === 'MUS'">
          <v-alert v-if="belowFulltime" type="error">You must select at least 11 credit hours of classes before you can continue. When an ensemble or lesson is added later, it will add 1 credit hour.</v-alert>
          <v-alert v-else-if="provisional && overFulltime" type="error">The maximum hours you can select is 14. Please remove classes from your schedule until you are at or under 14 before you can continue.</v-alert>
          <v-alert v-else-if="overFulltime" type="error">The maximum hours you can select is 17. Please remove classes from your schedule until you are at or under 17 before you can continue.</v-alert>
        </template>
        <template v-else-if="major === 'WSHP'">
          <v-alert v-if="belowFulltime" type="error">You must select at least 11 credit hours of classes before you can continue. When a music lesson is added later, it will add 1 credit hour.</v-alert>
          <v-alert v-else-if="provisional && overFulltime" type="error">The maximum hours you can select is 14. Please remove classes from your schedule until you are at or under 14 before you can continue.</v-alert>
          <v-alert v-else-if="overFulltime" type="error">The maximum hours you can select is 17. Please remove classes from your schedule until you are at or under 17 before you can continue.</v-alert>
        </template>
        <v-alert v-else-if="belowFulltime" type="error">You must select at least 12 credit hours of classes before you can continue.</v-alert>
        <v-alert v-else-if="provisional && overFulltime" type="error">The maximum hours you can select is 15. Please remove classes from your schedule until you are at or under 15 before you can continue.</v-alert>
        <v-alert v-else-if="overFulltime" type="error">The maximum hours you can select is 18. Please remove classes from your schedule until you are at or under 18 before you can continue.</v-alert>
      </v-col>
    </v-row>
    <v-card-actions>
      <v-btn :disabled="belowFulltime || overFulltime" color="primary" @click="next()">Continue</v-btn>
      <v-btn @click="$emit('back')">Back</v-btn>
    </v-card-actions>
  </v-card>
</template>
<style>
.v-calendar-daily_head-day-label {
  display: none;
}
.v-calendar-daily_head-weekday {
  margin: .7em auto;
}
</style>
<script>
import { computed, onMounted, ref, watch } from '@vue/composition-api'
export default {
  components: {
    RecommendedCourses: () => import('./courses/recommended'),
    SearchCourses: () => import('./courses/search')
  },
  props: {
    major: {
      type: String,
      required: true
    }
  },
  setup (props, { root, emit }) {
    const searchTab = ref(0)
    const scheduleTab = ref(1)
    const schedule = ref([])
    const courseSearchFilter = ref(['MSP143'])
    const provisional = ref(false)

    const calendar = ref(null)
    const startDate = computed(() => {
      let term = root.$store.state.term
      let year = term.substr(0, 4)
      let date = new Date(year + '-09-01')
      if (term.substr(4, 2) === '05') {
        date = new Date((parseInt(year) + 1) + '-01-01')
      }
      date.setDate(date.getDate() + 8 - date.getDay())
      return date.toISOString().substr(0, 10)
    })
    const weekdays = computed(() => {
      let hasSat = false
      schedule.value.forEach(({ meetingBase }) => {
        meetingBase.forEach(({ days }) => {
          if (days.indexOf('S') >= 0) hasSat = true
        })
      })
      return hasSat ? [1, 2, 3, 4, 5, 6] : [1, 2, 3, 4, 5]
    })

    const events = computed(() => {
      let dates = { M: startDate.value, T: '', W: '', R: '', F: '', S: '' }
      let dt = new Date(startDate.value)
      dt.setDate(dt.getDate() + 1)
      dates.T = dt.toISOString(0, 10)
      dt.setDate(dt.getDate() + 1)
      dates.W = dt.toISOString(0, 10)
      dt.setDate(dt.getDate() + 1)
      dates.R = dt.toISOString(0, 10)
      dt.setDate(dt.getDate() + 1)
      dates.F = dt.toISOString(0, 10)
      dt.setDate(dt.getDate() + 1)
      dates.S = dt.toISOString(0, 10)
      let arr = []
      for (const day of ['M', 'W', 'F']) {
        const start = new Date(dates[day])
        start.setHours(11)
        start.setMinutes(0)
        const end = new Date(dates[day])
        end.setHours(11)
        end.setMinutes(35)
        arr.push({ name: 'Chapel', timed: true, start, end, color: '#647da4' })
      }
      for (const { title, meetingBase, crn, section } of schedule.value) {
        for (const { days, startTime, endTime } of meetingBase) {
          for (let j = 0; j < days.length; j++) {
            if (days[j] in dates) {
              let obj = { name: title, timed: true, courseNum: title.split(' ')[0], crn, section }
              if (obj.courseNum === 'COR100') obj.color = '#647da4'
              obj.start = new Date(dates[days[j]])
              const [stHr, stMin] = startTime.split(':')
              obj.start.setHours(parseInt(stHr) + (stMin.substr(2) === 'pm' && stHr !== '12' ? 12 : 0))
              obj.start.setMinutes(stMin.substr(0, 2))
              obj.end = new Date(dates[days[j]])
              const [endHr, endMin] = endTime.split(':')
              obj.end.setHours(parseInt(endHr) + (endMin.substr(2) === 'pm' && endHr !== '12' ? 12 : 0))
              obj.end.setMinutes(endMin.substr(0, 2))
              arr.push(obj)
            }
          }
        }
      }
      return arr
    })
    const hours = computed(() => {
      let hours = 0
      for (const { credits } of schedule.value) hours += credits
      return hours
    })
    const belowFulltime = computed(() => {
      if (props.major === 'MUS' || props.major === 'WSHP') return hours.value < 11
      else return hours.value < 12
    })
    const overFulltime = computed(() => {
      const base = provisional.value ? 15 : 18
      if (props.major === 'MUS' || props.major === 'WSHP') return hours.value > base - 1
      else return hours.value > base
    })

    watch(calendar, () => {
      if (calendar.value) calendar.value.scrollToTime('08:00')
    })

    onMounted(async () => {
      const aggregate = [
        { $match: { term: root.$store.state.term, title: { $regex: 'COR1[05]0 Section 01' } } },
        { $addFields: { sortMeet: { $first: '$meetingBase' } } },
        { $match: { 'sortMeet.startTime': { $ne: '' } } },
        { $addFields: {
          sortDays: '$sortMeet.days',
          sortAmPm: {
            $substr: ['$sortMeet.startTime', { $subtract: [{ $strLenCP: '$sortMeet.startTime' }, 2] }, 2]
          },
          sortHour: {
            $toInt: {
              $replaceAll: {
                input: { $substr: ['$sortMeet.startTime', 0, 2] },
                find: ':',
                replacement: ''
              }
            }
          }
        } },
        { $sort: {
          sortDays: 1,
          sortAmPm: 1,
          sortHour: 1
        } }
      ]
      const { data } = await root.$feathers.service('calendar/classes').find({ query: { aggregate } })
      if (data.length > 0) {
        schedule.value.push(data[0])
      }
      if (root.$store.state.term === '') await root.$store.dispatch('loadTracker')
      if (['PV', 'LL'].includes(root.$store.state.academics.admitCode)) {
        provisional.value = true
      }
      scheduleTab.value = 0
    })

    const courseDetailOpen = ref(false)
    const selectedElement = ref(null)
    const selectedCourse = ref({})
    function showCourse ({ nativeEvent, event }) {
      const open = () => {
        if (event.name === 'Chapel') {
          selectedCourse.value = {
            title: 'Chapel',
            name: 'Chapel',
            meetingBase: [{ days: 'MWF', startTime: '11:00am', endTime: '11:35am' }],
            notes: 'Chapel meets every MWF at 11am, with some occasional bonus chapels at other days and times. Students are required to attend most chapels, so we have it on the calendar to help with planning.'
          }
        } else {
          const rec = { ...schedule.value.find(({ title }) => title === event.name) }
          rec.name = rec.title.substr(0, 6) + rec.title.substring(rec.title.indexOf(' - '))
          selectedCourse.value = rec
        }
        selectedElement.value = nativeEvent.target
        setTimeout(() => {
          courseDetailOpen.value = true
        }, 10)
      }

      if (courseDetailOpen.value) {
        courseDetailOpen.value = false
        setTimeout(open, 10)
      } else {
        open()
      }

      nativeEvent.stopPropagation()
    }

    function removeCourse (course) {
      for (let i = 0; i < schedule.value.length; i++) {
        if (schedule.value[i].crn === course.crn) {
          schedule.value.splice(i, 1)
          courseDetailOpen.value = false
          selectedElement.value = null
          selectedCourse.value = {}
          return
        }
      }
    }

    function dayDisplay (days) {
      const hash = { M: 'Mon', T: 'Tue', W: 'Wed', R: 'Thu', F: 'Fri' }
      const arr = []
      for (let i = 0; i < days.length; i++) {
        arr.push(hash[days[i]])
      }
      return arr.join('/')
    }

    function next () {
      if (props.major === 'MUS' || props.major === 'WSHP') {
        if (hours.value >= 11 && hours.value <= 17) {
          const courses = []
          for (const { crn } of schedule.value) courses.push(crn)
          emit('next', { courses })
        }
      } else if (hours.value >= 12 && hours.value <= 18) {
        const courses = []
        for (const { crn } of schedule.value) courses.push(crn)
        emit('next', { courses })
      }
    }

    let query = { term: root.$store.state.term, title: { $regex: 'COR100 Section 01' }, $limit: 5 }
    root.$feathers.service('calendar/classes').find({ query }).then(({ data }) => {
      if (data.length > 0) schedule.value.push(data[0])
    })

    return {
      searchTab,
      scheduleTab,
      schedule,
      courseSearchFilter,
      provisional,
      calendar,
      startDate,
      weekdays,
      events,
      hours,
      belowFulltime,
      overFulltime,
      courseDetailOpen,
      selectedElement,
      selectedCourse,
      showCourse,
      removeCourse,
      dayDisplay,
      next
    }
  }
}
</script>
